import React, { useEffect, useMemo, useRef, useState } from 'react';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import { Plus, Trash2, Upload, AlertTriangle } from 'lucide-react';
import WhatsAppTemplatePreview from './WhatsAppTemplatePreview';

const LANGUAGES = [
  { value: 'en_US', label: 'English (US)' },
  { value: 'en_GB', label: 'English (UK)' },
  { value: 'hi_IN', label: 'Hindi' },
  { value: 'es_ES', label: 'Spanish' },
  { value: 'pt_BR', label: 'Portuguese (Brazil)' },
  { value: 'fr_FR', label: 'French' },
  { value: 'de_DE', label: 'German' },
  { value: 'ar', label: 'Arabic' }
];

const CATEGORIES = [
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'UTILITY', label: 'Utility' },
  { value: 'AUTHENTICATION', label: 'Authentication' }
];

const emptyButton = () => ({ type: 'QUICK_REPLY', text: '', url: '', phone_number: '' });

const HEADER_ACCEPT = {
  IMAGE: 'image/jpeg,image/png',
  VIDEO: 'video/mp4,video/3gpp',
  DOCUMENT: 'application/pdf'
};

const HEADER_MAX_BYTES = {
  IMAGE: 5 * 1024 * 1024,
  VIDEO: 16 * 1024 * 1024,
  DOCUMENT: 100 * 1024 * 1024
};

const HEADER_LABELS = {
  IMAGE: 'JPEG or PNG (max 5 MB)',
  VIDEO: 'MP4 (max 16 MB)',
  DOCUMENT: 'PDF (max 100 MB)'
};

function mediaHeaderOptions(category) {
  const options = [
    { value: 'none', label: 'None' },
    { value: 'TEXT', label: 'Text' }
  ];
  if (category !== 'AUTHENTICATION') {
    options.push(
      { value: 'IMAGE', label: 'Image' },
      { value: 'VIDEO', label: 'Video' },
      { value: 'DOCUMENT', label: 'Document (PDF)' }
    );
  }
  return options;
}

function extractHeaderHandle(header) {
  if (!header?.example) return null;
  const raw = header.example.header_handle;
  if (Array.isArray(raw) && raw.length > 0) return raw[0];
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  return null;
}

function parseTemplateToForm(template) {
  const components = template?.components || [];
  const header = components.find((c) => c.type === 'HEADER');
  const body = components.find((c) => c.type === 'BODY');
  const footer = components.find((c) => c.type === 'FOOTER');
  const buttonsComp = components.find((c) => c.type === 'BUTTONS');

  let headerType = 'none';
  let headerText = '';
  if (header) {
    const fmt = (header.format || 'TEXT').toUpperCase();
    headerType = fmt;
    if (fmt === 'TEXT') headerText = header.text || '';
  }

  const buttons = (buttonsComp?.buttons || []).map((b) => ({
    type: b.type,
    text: b.text || '',
    url: b.url || '',
    phone_number: b.phone_number || ''
  }));

  const headerHandle = extractHeaderHandle(header);

  return {
    name: template?.name || '',
    language: template?.language || 'en_US',
    category: template?.category || 'MARKETING',
    headerType: headerType === 'TEXT' && !header?.format ? 'TEXT' : headerType,
    headerText,
    headerHandle,
    headerPreviewUrl: null,
    headerFileName: headerHandle
      ? `Saved ${headerType.toLowerCase()} sample on Meta`
      : null,
    headerMediaSource: headerHandle ? 'existing' : 'new',
    bodyText: body?.text || '',
    bodyExamples: body?.example?.body_text?.[0] || [],
    footerText: footer?.text || '',
    buttons: buttons.length ? buttons : []
  };
}

function canEditMetaTemplateStatus(status) {
  return ['APPROVED', 'REJECTED', 'PAUSED'].includes((status || '').toUpperCase());
}

const WhatsAppTemplateForm = ({
  mode = 'create',
  initialTemplate = null,
  formInitial = null,
  workspaceId,
  onSuccess,
  onCancel
}) => {
  const isEdit = mode === 'edit';
  const status = initialTemplate?.status || '';
  const lockedMetaFields = isEdit && status === 'APPROVED';
  const editBlockedByMeta = isEdit && !canEditMetaTemplateStatus(status);

  const [form, setForm] = useState(() => {
    const base = {
      headerPreviewUrl: null,
      headerFileName: null,
      buttons: []
    };
    if (formInitial) return { ...base, ...formInitial, buttons: formInitial.buttons || [] };
    if (initialTemplate) return { ...base, ...parseTemplateToForm(initialTemplate) };
    return {
      ...base,
      name: '',
      language: 'en_US',
      category: 'MARKETING',
      headerType: 'none',
      headerText: '',
      headerHandle: null,
      headerPreviewUrl: null,
      headerFileName: null,
      headerMediaSource: 'new',
      bodyText: '',
      bodyExamples: [],
      footerText: ''
    };
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [canUseExistingMedia, setCanUseExistingMedia] = useState(() => {
    if (!initialTemplate || !isEdit) return false;
    const header = (initialTemplate.components || []).find((c) => c.type === 'HEADER');
    return !!extractHeaderHandle(header);
  });
  const previewUrlRef = useRef(form.headerPreviewUrl);
  const savedHeaderHandleRef = useRef(form.headerHandle || null);
  const editHydratedRef = useRef(false);

  useEffect(() => {
    if (!isEdit || !initialTemplate || !workspaceId || editHydratedRef.current) return undefined;

    let cancelled = false;
    const hydrateFromMeta = async () => {
      try {
        const id = initialTemplate.template_id || initialTemplate.id;
        const res = await apiClient.get(
          `/api/whatsapp/templates/manage/${encodeURIComponent(id)}`,
          {
            headers: { 'x-workspace-id': workspaceId },
            params: { name: initialTemplate.name, language: initialTemplate.language }
          }
        );
        if (cancelled) return;
        const parsed = parseTemplateToForm(res.data.template);
        savedHeaderHandleRef.current = parsed.headerHandle || null;
        setCanUseExistingMedia(!!parsed.headerHandle);
        editHydratedRef.current = true;
        setForm((prev) => ({
          ...prev,
          headerType: parsed.headerType,
          headerText: parsed.headerText,
          headerHandle: parsed.headerHandle,
          headerFileName: parsed.headerFileName,
          headerMediaSource: parsed.headerMediaSource,
          bodyText: parsed.bodyText || prev.bodyText,
          bodyExamples: parsed.bodyExamples?.length ? parsed.bodyExamples : prev.bodyExamples,
          footerText: parsed.footerText ?? prev.footerText,
          buttons: parsed.buttons?.length ? parsed.buttons : prev.buttons
        }));
      } catch {
        editHydratedRef.current = true;
      }
    };

    hydrateFromMeta();
    return () => {
      cancelled = true;
    };
  }, [isEdit, initialTemplate, workspaceId]);

  useEffect(() => {
    previewUrlRef.current = form.headerPreviewUrl;
  }, [form.headerPreviewUrl]);

  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const headerOptions = useMemo(
    () => mediaHeaderOptions(form.category),
    [form.category]
  );

  const hasMediaHeader = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerType);
  const useExistingMedia = form.headerMediaSource === 'existing' && !!form.headerHandle;

  const bodyVarCount = useMemo(() => {
    const matches = form.bodyText.match(/\{\{(\d+)\}\}/g) || [];
    return [...new Set(matches.map((m) => parseInt(m.replace(/\D/g, ''), 10)))].sort((a, b) => a - b);
  }, [form.bodyText]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleCategoryChange = (category) => {
    setForm((prev) => {
      const next = { ...prev, category };
      if (category === 'AUTHENTICATION' && hasMediaHeaderType(prev.headerType)) {
        if (prev.headerPreviewUrl) URL.revokeObjectURL(prev.headerPreviewUrl);
        next.headerType = 'none';
        next.headerText = '';
        next.headerHandle = null;
        next.headerPreviewUrl = null;
        next.headerFileName = null;
      }
      return next;
    });
  };

  function hasMediaHeaderType(type) {
    return ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(type);
  }

  const handleHeaderTypeChange = (headerType) => {
    setForm((prev) => {
      if (prev.headerPreviewUrl) URL.revokeObjectURL(prev.headerPreviewUrl);
      return {
        ...prev,
        headerType,
        headerText: '',
        headerHandle: null,
        headerPreviewUrl: null,
        headerFileName: null,
        headerMediaSource: 'new'
      };
    });
    savedHeaderHandleRef.current = null;
  };

  const handleHeaderMediaSourceChange = (source) => {
    if (source === 'existing') {
      const saved = savedHeaderHandleRef.current;
      if (!saved) {
        toast.error('No saved sample found on Meta for this template');
        return;
      }
      setForm((prev) => {
        if (prev.headerPreviewUrl) URL.revokeObjectURL(prev.headerPreviewUrl);
        return {
          ...prev,
          headerMediaSource: 'existing',
          headerHandle: saved,
          headerPreviewUrl: null,
          headerFileName: `Saved ${prev.headerType.toLowerCase()} sample on Meta`
        };
      });
      return;
    }

    setForm((prev) => {
      if (prev.headerPreviewUrl) URL.revokeObjectURL(prev.headerPreviewUrl);
      return {
        ...prev,
        headerMediaSource: 'new',
        headerHandle: null,
        headerPreviewUrl: null,
        headerFileName: null
      };
    });
  };

  const insertVariable = () => {
    const next = bodyVarCount.length ? Math.max(...bodyVarCount) + 1 : 1;
    updateField('bodyText', `${form.bodyText}{{${next}}}`);
  };

  const handleHeaderFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !workspaceId) return;

    const format = form.headerType;
    const maxSize = HEADER_MAX_BYTES[format];
    if (maxSize && file.size > maxSize) {
      toast.error(`File too large. ${HEADER_LABELS[format]}`);
      e.target.value = '';
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setForm((prev) => {
      if (prev.headerPreviewUrl) URL.revokeObjectURL(prev.headerPreviewUrl);
      return {
        ...prev,
        headerPreviewUrl: previewUrl,
        headerFileName: file.name,
        headerHandle: null
      };
    });

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('format', format);
      const res = await apiClient.post('/api/whatsapp/templates/manage/upload-header', fd, {
        headers: {
          'x-workspace-id': workspaceId,
          'Content-Type': 'multipart/form-data'
        }
      });
      updateField('headerHandle', res.data.header_handle);
      savedHeaderHandleRef.current = res.data.header_handle;
      setForm((prev) => ({
        ...prev,
        headerMediaSource: 'new',
        headerFileName: file.name
      }));
      toast.success('Header media uploaded to Meta');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
      setForm((prev) => {
        if (prev.headerPreviewUrl) URL.revokeObjectURL(prev.headerPreviewUrl);
        return { ...prev, headerPreviewUrl: null, headerFileName: null, headerHandle: null };
      });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editBlockedByMeta) {
      toast.error('Meta does not allow editing this template in its current status.');
      return;
    }
    if (!form.bodyText.trim()) {
      toast.error('Body text is required');
      return;
    }
    if (!isEdit && !form.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerType) && !form.headerHandle) {
      toast.error('Upload a file for the media header');
      return;
    }

    setSubmitting(true);
    const headers = { 'x-workspace-id': workspaceId };
    const {
      headerPreviewUrl: _preview,
      headerFileName: _fileName,
      headerMediaSource: _mediaSource,
      ...formFields
    } = form;
    const payload = {
      ...formFields,
      name: form.name.trim().toLowerCase(),
      buttons: form.buttons.filter((b) => b.text?.trim())
    };

    try {
      if (isEdit) {
        const res = await apiClient.put(
          `/api/whatsapp/templates/manage/${initialTemplate.template_id || initialTemplate.id}`,
          payload,
          { headers }
        );
        if (!onSuccess) toast.success('Template updated on Meta');
        onSuccess?.({
          name: initialTemplate.name,
          language: initialTemplate.language,
          status: res.data.template?.status || initialTemplate.status,
          message: res.data.message
        });
      } else {
        const res = await apiClient.post('/api/whatsapp/templates/manage', payload, { headers });
        const tpl = res.data.template || {};
        if (!onSuccess) toast.success(res.data.message || 'Template submitted to Meta');
        onSuccess?.({
          name: payload.name,
          language: payload.language,
          status: tpl.status || 'PENDING',
          message: res.data.message
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save template');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-1 min-h-0 min-w-0 flex-col">
      <div className="flex flex-1 min-h-0 min-w-0 overflow-hidden">
        <div className="flex-1 min-w-0 overflow-y-auto p-5 space-y-4">
      {isEdit && editBlockedByMeta && (
        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <p>
              This template is <strong>{status}</strong>. Meta does not allow edits while it is under review.
            </p>
            <p className="mt-1 text-xs text-blue-800">
              Wait for Meta to approve or reject it, or close this panel and use <strong>Duplicate</strong> on the list to create a copy with a new name.
            </p>
          </div>
        </div>
      )}

      {isEdit && !editBlockedByMeta && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            {lockedMetaFields ? (
              <p>
                This template is <strong>APPROVED</strong>. Name, language, and category cannot be changed.
                Meta limits how often approved templates can be edited.
              </p>
            ) : (
              <p>Name, language, and category cannot be changed after creation. Editing resubmits content to Meta.</p>
            )}
            {status && (
              <p className="mt-1 text-xs">
                Current status: <strong>{status}</strong>
              </p>
            )}
          </div>
        </div>
      )}

      <fieldset disabled={editBlockedByMeta} className="space-y-4 min-w-0 disabled:opacity-60">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Template name</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => updateField('name', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
            disabled={isEdit}
            placeholder="my_template_name"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            required={!isEdit}
          />
          {!isEdit && (
            <p className="text-xs text-gray-500 mt-1">Lowercase letters, numbers, underscores only</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
          <select
            value={form.language}
            onChange={(e) => updateField('language', e.target.value)}
            disabled={isEdit}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
          >
            {LANGUAGES.map((l) => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select
            value={form.category}
            onChange={(e) => handleCategoryChange(e.target.value)}
            disabled={lockedMetaFields}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          {form.category === 'MARKETING' && (
            <p className="text-xs text-purple-700 bg-purple-50 border border-purple-100 rounded-lg px-2 py-1.5 mt-1.5">
              Marketing templates support image, video, or PDF headers. Upload a sample file in Header — Meta uses it for review; you send real media when messaging.
            </p>
          )}
          {form.category === 'AUTHENTICATION' && (
            <p className="text-xs text-gray-600 mt-1">Authentication templates use text headers only (OTP / verification).</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Header (optional)</label>
          <select
            value={form.headerType}
            onChange={(e) => handleHeaderTypeChange(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            {headerOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {form.headerType === 'TEXT' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Header text</label>
          <input
            type="text"
            value={form.headerText}
            onChange={(e) => updateField('headerText', e.target.value)}
            maxLength={60}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Optional header line"
          />
        </div>
      )}

      {hasMediaHeader && (
        <div className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50/50">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Header media sample *
            </label>
            <p className="text-xs text-gray-500 mb-2">
              {HEADER_LABELS[form.headerType]} — required for Meta template review.
            </p>

            {isEdit && canUseExistingMedia && (
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">Media sample</label>
                <select
                  value={form.headerMediaSource || 'new'}
                  onChange={(e) => handleHeaderMediaSourceChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="existing">Use saved sample on Meta</option>
                  <option value="new">Upload new sample</option>
                </select>
              </div>
            )}

            {useExistingMedia ? (
              <div className="flex items-center gap-2 px-3 py-2.5 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                <span className="shrink-0">✓</span>
                <span>
                  {form.headerFileName || `Saved ${form.headerType.toLowerCase()} on Meta`}
                  <span className="text-green-600 ml-1">· Ready for Meta</span>
                </span>
              </div>
            ) : (
              <>
                <label className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-white text-sm bg-white">
                  <Upload className="h-4 w-4" />
                  {uploading ? 'Uploading to Meta…' : form.headerHandle ? 'Replace file' : 'Choose file'}
                  <input
                    type="file"
                    className="hidden"
                    accept={HEADER_ACCEPT[form.headerType]}
                    onChange={handleHeaderFile}
                    disabled={uploading}
                  />
                </label>
                {form.headerFileName && (
                  <p className="text-xs text-gray-600 mt-2 truncate">
                    {form.headerFileName}
                    {form.headerHandle && (
                      <span className="text-green-600 ml-2">· Ready for Meta</span>
                    )}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700">Body *</label>
          <button
            type="button"
            onClick={insertVariable}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            + Add variable {'{{n}}'}
          </button>
        </div>
        <textarea
          value={form.bodyText}
          onChange={(e) => updateField('bodyText', e.target.value)}
          rows={4}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          placeholder="Hello {{1}}, your order is ready."
          required
        />
        {bodyVarCount.length > 0 && (
          <div className="mt-2 space-y-1">
            <p className="text-xs text-gray-500">Sample values for Meta review:</p>
            {bodyVarCount.map((num, idx) => (
              <input
                key={num}
                type="text"
                value={form.bodyExamples[idx] || ''}
                onChange={(e) => {
                  const examples = [...(form.bodyExamples || [])];
                  examples[idx] = e.target.value;
                  updateField('bodyExamples', examples);
                }}
                placeholder={`Sample for {{${num}}}`}
                className="w-full border border-gray-200 rounded px-2 py-1 text-sm"
              />
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Footer (optional)</label>
        <input
          type="text"
          value={form.footerText}
          onChange={(e) => updateField('footerText', e.target.value)}
          maxLength={60}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">Buttons (optional)</label>
          <button
            type="button"
            onClick={() => updateField('buttons', [...form.buttons, emptyButton()])}
            className="text-xs flex items-center gap-1 text-blue-600"
          >
            <Plus className="h-3 w-3" /> Add button
          </button>
        </div>
        {form.buttons.map((btn, idx) => (
          <div key={idx} className="flex flex-wrap gap-2 mb-2 items-end border border-gray-100 p-2 rounded-lg">
            <select
              value={btn.type}
              onChange={(e) => {
                const buttons = [...form.buttons];
                buttons[idx] = { ...buttons[idx], type: e.target.value };
                updateField('buttons', buttons);
              }}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            >
              <option value="QUICK_REPLY">Quick reply</option>
              <option value="URL">URL</option>
              <option value="PHONE_NUMBER">Phone</option>
            </select>
            <input
              type="text"
              placeholder="Button label"
              value={btn.text}
              onChange={(e) => {
                const buttons = [...form.buttons];
                buttons[idx] = { ...buttons[idx], text: e.target.value };
                updateField('buttons', buttons);
              }}
              className="flex-1 min-w-[120px] border border-gray-300 rounded px-2 py-1 text-sm"
            />
            {btn.type === 'URL' && (
              <input
                type="url"
                placeholder="https://..."
                value={btn.url}
                onChange={(e) => {
                  const buttons = [...form.buttons];
                  buttons[idx] = { ...buttons[idx], url: e.target.value };
                  updateField('buttons', buttons);
                }}
                className="flex-1 min-w-[160px] border border-gray-300 rounded px-2 py-1 text-sm"
              />
            )}
            {btn.type === 'PHONE_NUMBER' && (
              <input
                type="text"
                placeholder="+91..."
                value={btn.phone_number}
                onChange={(e) => {
                  const buttons = [...form.buttons];
                  buttons[idx] = { ...buttons[idx], phone_number: e.target.value };
                  updateField('buttons', buttons);
                }}
                className="flex-1 min-w-[120px] border border-gray-300 rounded px-2 py-1 text-sm"
              />
            )}
            <button
              type="button"
              onClick={() => updateField('buttons', form.buttons.filter((_, i) => i !== idx))}
              className="p-1 text-red-500 hover:bg-red-50 rounded"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      </fieldset>

        </div>

        <div className="w-72 lg:w-80 shrink-0 border-l border-gray-200 bg-[#f0f2f5] p-3 overflow-y-auto overflow-x-hidden hidden md:flex md:flex-col md:items-center">
          <WhatsAppTemplatePreview form={form} sidebar />
        </div>
      </div>

      <div className="md:hidden border-t border-gray-200 bg-[#f0f2f5] p-4 max-h-72 overflow-y-auto shrink-0">
        <WhatsAppTemplatePreview form={form} compact />
      </div>

      <div className="flex justify-end gap-3 px-5 py-3 border-t border-gray-200 bg-gray-50 shrink-0">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting || editBlockedByMeta}
          className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Saving…' : isEdit ? 'Update template' : 'Create template'}
        </button>
      </div>
    </form>
  );
};

export { parseTemplateToForm, canEditMetaTemplateStatus };

export default WhatsAppTemplateForm;
